import { useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { LockIcon, MailIcon } from 'lucide-react';
import {
  AuthShell,
  BrandMark,
  Button,
  Callout,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  IconInput,
  PasswordInput,
} from '@opengoal/ui';
import { ApiError, normalizeAuthError } from '@opengoal/http';
import { useTranslation } from 'react-i18next';
import { env } from '@/app/env';
import { authClient } from '@/lib/auth-client';
import { setSessionToken } from '@/lib/session';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: '/_auth/login' });
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const schema = z.object({
    email: z.string().trim().min(1, t('auth.emailRequired')).email(t('auth.emailInvalid')),
    password: z.string().min(1, t('auth.passwordRequired')),
  });
  type LoginFormValues = z.infer<typeof schema>;

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const signIn = useMutation({
    mutationFn: async (values: LoginFormValues) => {
      const { data, error: signInError } = await authClient.signIn.email({
        email: values.email,
        password: values.password,
      });

      // better-auth resolves rather than rejects on failure, so the error has
      // to be re-thrown for TanStack Query to treat this as a failed mutation.
      if (signInError) throw normalizeAuthError(signInError, 401);
      if (!data?.token) throw new ApiError({ status: 500, message: t('auth.errors.generic') });

      return data.token;
    },
    onSuccess: async (token) => {
      setSessionToken(token);
      // The previous session's cached profile must not survive into this one.
      // Sign-in also resolves the caller's active agency server-side, so
      // /users/me is only meaningful once the new token is in place.
      await queryClient.invalidateQueries();
      void navigate({ to: search.next ?? '/' });
    },
    onError: (mutationError: unknown) => {
      setError(messageFor(mutationError, t));
    },
  });

  return (
    <AuthShell
      brand={<BrandMark name={env.appName} tagline={t('app.tagline')} />}
      title={t('auth.signInTitle')}
      subtitle={t('auth.signInSubtitle')}
      aside={{ heading: t('auth.asideHeading'), body: t('auth.asideBody') }}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link to="/forgot-password" className="text-muted-foreground hover:text-foreground">
            {t('auth.forgotPassword')}
          </Link>
          <span className="text-muted-foreground">
            {t('auth.noAccount')}{' '}
            <Link to="/signup" className="font-medium text-foreground hover:underline">
              {t('auth.createAgencyLink')}
            </Link>
          </span>
        </div>
      }
    >
      <Form {...form}>
        <form
          noValidate
          className="grid gap-5"
          onSubmit={form.handleSubmit((values) => {
            setError(null);
            signIn.mutate(values);
          })}
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.email')}</FormLabel>
                <FormControl>
                  <IconInput
                    icon={<MailIcon />}
                    type="email"
                    autoComplete="email"
                    placeholder="you@agency.com"
                    className="h-10"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.password')}</FormLabel>
                <FormControl>
                  <PasswordInput
                    icon={<LockIcon />}
                    autoComplete="current-password"
                    className="h-10"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {error ? <Callout variant="destructive" title={error} /> : null}

          <Button type="submit" size="lg" className="w-full" loading={signIn.isPending}>
            {t('auth.signIn')}
          </Button>
        </form>
      </Form>
    </AuthShell>
  );
}

/**
 * Maps a failure onto copy the user can act on.
 *
 * Branches on status rather than on the message text, which is prose and
 * changes between better-auth releases. A wrong password and an unknown email
 * deliberately produce the same wording — distinguishing them would let anyone
 * test which addresses have accounts.
 */
function messageFor(error: unknown, t: (key: string) => string): string {
  if (error instanceof ApiError) {
    // A self-signup that never confirmed its address: the backend has just
    // re-sent the link, so say so rather than "wrong password".
    if (error.code === 'EMAIL_NOT_VERIFIED') return t('auth.errors.emailNotVerified');
    if (error.status === 401 || error.status === 403) return t('auth.errors.invalidCredentials');
    if (error.status === 429) return t('auth.errors.tooManyAttempts');
  }
  return t('auth.errors.generic');
}
